<template>
  <SeasonMobileBurnItem
    v-if="$device.isMobile"
    :token="token"
    :rate="rate"
    :balance="balance"
    :max-points="formatAmountFloor(props.seasonToken.seasonPoints)"
    :burn-card-click-handler="burnCardClickHandler"
    :season-token="seasonToken"
    :token-id="tokenId"
  />

  <el-tooltip
    v-else
    :disabled="Number(balance) > 0"
    effect="dark"
    :popper-class="`notEnoughTooltipContainer tw:max-w-sm tw:leading-loose tw:text-center ${$i18n.locale === 'ru' ? 'ru-locale' : ''}`"
  >
    <template #content>
      <div>
        {{ $t('notEnoughToExchange') }}
      </div>
    </template>

    <div
      class="BurnCardcontainer"
      :class="{ 'burn-card-container-ru': $i18n.locale === 'ru' }"
      @click="burnCardClickHandler"
    >
      <div class="imgContainer">
        <img width="82" :src="imgSrc" alt="" />
      </div>
      <div
        v-if="
          tokenMetaData(blockchain.contracts, seasonToken.tokenAddress).interface === 'erc20' ||
          !BigNumber(balance).isZero()
        "
        class="rateBlock"
      >
        <p>{{ $t('appStatisticRate') }}:</p>
        <p class="rate">
          {{ props.seasonToken.tokenMultiplier || 1 }} =
          {{ BigNumber(rate).integerValue(BigNumber.ROUND_FLOOR).toNumber() }}
          <img width="24" :src="tokens.seasonPoints" alt="" />
        </p>
      </div>

      <p class="balance">{{ availableTokensFormatted }}</p>
      <SeasonBurnCardLabel :max-amount-to-exchange="formatAmountFloor(props.seasonToken.seasonPoints)" />
    </div>
  </el-tooltip>

  <el-dialog
    v-model="showExchangePopup"
    :title="
      $t('appStatisticExchangePopupTitle', {
        pairLabel: capitalize($t(tokensConfig.seasonPoints.label, { count: 3 }).toLowerCase())
      })
    "
    modal-class="modal"
    :append-to-body="true"
    :fullscreen="$device.isMobile"
    class="exchangeModal"
    :class="{ exchangeModalMobile: $device.isMobile, 'ru-locale': $i18n.locale === 'ru' }"
    destroy-on-close
  >
    <ExchangeModal
      :token-balance="BigNumber(balance).toString()"
      :rate="BigNumber(rate).integerValue(BigNumber.ROUND_FLOOR).toNumber()"
      :token-address="props.seasonToken.tokenAddress"
      :token-multiplier="props.seasonToken.tokenMultiplier"
      :token-pair="tokensConfig.seasonPoints.name.contract"
      :icon="imgSrc"
      :max-amount="tokenId ? 1 : maxAmountTokens"
      :exchange="exchange"
      @close-modal="showExchangePopup = false"
    />
  </el-dialog>
</template>
<script lang="ts" setup>
import { useWeb3Modal, useWeb3ModalAccount, useWeb3ModalProvider } from '@web3modal/ethers/vue';
import { zeroPadValue } from 'ethers';
import { ERC721__factory, Season__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import tokens from '~/public/img/tokens';
import type { TSeasonConfigItem } from '~/types/season';
import { tokensConfig } from '~/utils/constants';
import { BigNumber } from 'bignumber.js';
import { capitalize } from 'element-plus/es/utils/strings.mjs';

const { blockchain } = useEnvs();
const { $getIconToken } = useNuxtApp();
const { address } = useWeb3ModalAccount();
const { open } = useWeb3Modal();
const { getProvider } = useProvider();
const { walletProvider } = useWeb3ModalProvider();
const { sendContractMethod } = useSendContractMethod();
const store = useMainStore();
const { t } = useI18n();
const showExchangePopup = ref(false);
const { getSigner } = useProvider();

const props = defineProps<{
  seasonToken: TSeasonConfigItem;
  seasonId: number;
  tokenValue?: number;
  tokenId?: number;
}>();

const isTokenImageLoading = ref<boolean>(!!props.tokenId);

const token = computed(() => tokenMetaData(blockchain.contracts, props.seasonToken.tokenAddress));

const balance = computed(() => {
  if (props.tokenId) return '1';
  return props.seasonToken?.balance || '0';
});

const rate = computed(() =>
  props.tokenId && props.tokenValue
    ? BigNumber(props.seasonToken.pointsMultiplier).multipliedBy(props.tokenValue).toString()
    : props.seasonToken.pointsMultiplier
);

const tokenURIImage = asyncComputed(async () => {
  const tokenContract = ERC721__factory.connect(props.seasonToken.tokenAddress, getProvider());

  const tokenUri = await tokenContract.tokenURI(props.tokenId || 0);
  const image = await $fetch<{ image_url: string }>(tokenUri);

  isTokenImageLoading.value = false;

  return image.image_url;
});

const imgSrc = computed<any>(() => {
  return props.tokenId ? tokenURIImage.value : $getIconToken(props.seasonToken);
});

const availableTokensFormatted = computed(() => formatAmountFloor(Number(balance.value)));
const maxAmountTokens = computed(() =>
  BigNumber(Math.floor(Number(balance.value)))
    .minus(BigNumber(Math.floor(Number(balance.value))).toNumber() % props.seasonToken.tokenMultiplier)
    .toNumber()
);
const burnCardClickHandler = () => {
  if (!address.value || !walletProvider.value) {
    open();
    return;
  }
  if (Number(balance.value) === 0) return;

  showExchangePopup.value = true;
};

const exchange = async (quantity: number) => {
  if (!address.value || !walletProvider.value) {
    return;
  }

  const tx = await sendContractMethod({
    contract: Season__factory.connect(blockchain.contracts.season, await getSigner()),
    methodName: 'useSeason(bytes32[])',
    args: [
      [
        zeroPadValue(
          `0x${
            props.tokenId
              ? BigNumber(props.tokenId).toNumber().toString(16).padStart(24, '0')
              : BigNumber(quantity).dividedBy(props.seasonToken.tokenMultiplier).toString(16).padStart(24, '0')
          }${props.seasonToken.tokenAddress?.slice(2)}`,
          32
        )
      ]
    ]
  });

  if (!tx?.hash) return;

  saveHashToLocalStorage(
    `${t('appStatisticExchangeButton')} ${quantity} ${token.value.methodName.toUpperCase()}`,
    tx.hash
  );

  store.updateVersion();
  await tx.wait();

  modifyHistoryHashStatus(tx.hash, 'Done');
  showExchangePopup.value = false;
  store.updateVersion();
};
</script>

<style lang="scss" scoped>
.BurnCardcontainer {
  background-color: #041a27;
  min-height: 184px;

  &:hover {
    background-image: radial-gradient(circle closest-corner at 50% 100%, #3b7285, rgba(0, 0, 0, 0));
  }

  border: 1px solid #1e353e;
  border-radius: 12px;
  filter: drop-shadow(0 0 0.75rem rgba(0, 0, 0, 0.7));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 20px 3px 10px;
  font-size: 24px;
  color: #90989d;
  line-height: 1;
  position: relative;
  cursor: pointer;
  max-height: 184px;

  .balance {
    font-size: 26px;
    color: white;
  }

  .rateBlock {
    text-align: center;
  }

  .imgContainer {
    min-height: 82px;
    height: 82px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 12px;
    }
  }

  .rate {
    display: flex;
    padding-left: 10px;
    flex-wrap: nowrap;
    text-wrap: nowrap;
    align-items: center;
    > img {
      margin-bottom: -5%;
    }
  }
}

.burn-card-container-ru {
  font-size: 19px;
}
</style>

<style lang="scss">
.el-popper.notEnoughTooltipContainer {
  border: 1px solid #1e353e !important;
  border-radius: 15px;
  background-color: rgba(0, 0, 0, 0.85) !important;
  font-size: 24px;
  color: white;
  font-family: Grenze, sans-serif;
  padding: 16px;
}
</style>
